/**
 * Reading-surface text helpers (reading-surface-spec §1, §3). Ported from the local app's
 * Transcript.jsx: whitespace tokenization, Unicode punctuation trim, deterministic speaker hue.
 *
 * M4a: the tokenizer itself moved to `packages/worker/src/lib/tokenize.ts` (aliased here as
 * `@nelnela/tokenize`) so the client's tap targets and the resolver's `line_item.char_start` are
 * produced by the SAME code — one implementation, no parity to maintain. Re-exported from here so
 * every existing import keeps working.
 */
export {
  WORD_TRIM,
  tokenize,
  isWhitespace,
  wordTokens,
  surfaceKey,
  type Token,
  type WordToken,
} from '@nelnela/tokenize';

/** mm:ss (or h:mm:ss past an hour), tabular — the time gutter. */
export function fmtTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * Sizes for the media lane (M4c): the wizard's "this file is 1.4 GB", Me's "21.4 GB stored",
 * the Library chip's "upload · 1.2 GB".
 *
 * Base 1000, not 1024: the number has to agree with what Jeff's file manager tells him about the
 * file he is uploading (macOS/iOS have been decimal since 10.6) and with what R2 bills per GB.
 * One decimal at a GB and up, where the difference is money; whole units below, where it is noise.
 */
const BYTE_UNITS = [
  { limit: 1e12, suffix: 'TB', decimals: 1 },
  { limit: 1e9, suffix: 'GB', decimals: 1 },
  { limit: 1e6, suffix: 'MB', decimals: 0 },
  { limit: 1e3, suffix: 'KB', decimals: 0 },
] as const;

export function formatBytes(bytes: number): string {
  const n = Number.isFinite(bytes) ? Math.max(0, bytes) : 0;
  for (let i = 0; i < BYTE_UNITS.length; i++) {
    const u = BYTE_UNITS[i];
    if (n < u.limit) continue;
    const shown = (n / u.limit).toFixed(u.decimals);
    // Rounding can carry into the next unit up: 999_950 B is "1 MB", never "1000 KB".
    if (Number(shown) >= 1000 && i > 0) {
      const up = BYTE_UNITS[i - 1];
      return `${(n / up.limit).toFixed(up.decimals)} ${up.suffix}`;
    }
    return `${shown} ${u.suffix}`;
  }
  return `${Math.round(n)} B`;
}

/**
 * M4c: "12 videos · 21.4 GB stored" — the one wording for what the uploads occupy, shared by Me's
 * card and the Add wizard (which appends "· this file is 1.4 GB"). Report tone, never a bar:
 * there is no quota, so a bar would invent a limit that does not exist.
 *
 * No trailing period: the wizard suffixes it. Zero reads as a sentence rather than "0 videos ·
 * 0 B" — Jeff sees this line before his first upload, and a row of zeros looks like a bug.
 */
export function mediaUsageLine(usage: { objects: number; usedBytes: number }): string {
  if (usage.objects === 0) return 'Nothing stored yet';
  const n = `${usage.objects} ${usage.objects === 1 ? 'video' : 'videos'}`;
  return `${n} · ${formatBytes(usage.usedBytes)} stored`;
}

/** R2 standard storage in USD per GB-month. Cloudflare's published price as checked on
 *  2026-08-18 — dated on purpose: prices in this repo are dated and re-verified, never quoted as
 *  current. If it moves, move this and the date together. */
export const R2_USD_PER_GB_MONTH = 0.015;

/**
 * M4c: "≈ $0.32/mo" — what the bucket costs. This is the reason Me shows usage at all: with no
 * quota to fill, the only number worth reading is the bill. Decimal GB, not GiB, because that is
 * how R2 meters GB-months — and it agrees with `formatBytes` above. Anything that would round to
 * $0.00 says so in words instead of pretending to be free.
 */
export function mediaCostLine(usedBytes: number): string {
  const usd = (Math.max(0, usedBytes) / 1e9) * R2_USD_PER_GB_MONTH;
  return usd < 0.005 ? 'under $0.01/mo' : `≈ $${usd.toFixed(2)}/mo`;
}

/** Deterministic hue for a speaker chip (Transcript.jsx `speakerHue`, verbatim). */
export function speakerHue(name: string): number {
  let h = 0;
  for (const c of name) h = (h * 31 + (c.codePointAt(0) ?? 0)) % 997;
  return (h * 47) % 360;
}
